import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_europe-guide/artifacts/rvgxr6ue_parrot.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Explore Countries', href: '/#countries' },
    { name: 'Live Sessions', href: '/sessions' },
    { name: '1:1 Call', href: '/book-call' },
    { name: 'Profile Evaluation', href: '/profile-evaluation' },
  ];

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <img 
              src={LOGO_URL} 
              alt="TieClad" 
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-[#002A76] hover:text-[#0AA06A] font-medium transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/profile-evaluation">
              <Button 
                data-testid="nav-cta-button"
                className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full px-6 py-2 font-semibold btn-hover-lift"
              >
                Get Free Evaluation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X size={24} className="text-[#002A76]" /> : <Menu size={24} className="text-[#002A76]" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg transform transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="block text-[#002A76] hover:text-[#0AA06A] font-medium py-2 transition-colors"
            >
              {link.name}
            </Link>
          ))}
          <Link to="/profile-evaluation" className="block pt-4">
            <Button 
              data-testid="mobile-cta-button"
              className="w-full bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full py-3 font-semibold"
            >
              Get Free Evaluation
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
